import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { format } from "date-fns";
import { enUS } from "date-fns/locale";
import { Link } from "wouter";
import { Gift, MapPin, Loader2, CheckCircle2, ArrowLeft, Wallet as WalletIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import {
  MobileSelect,
  MobileSelectItem,
} from "@/components/ui/mobile-select";
import { MapPicker } from "@/components/MapPicker";
import { cn } from "@/lib/utils";
import knetLogo from "@assets/knet-logo.png";
import creditCardLogos from "@assets/credit-card-logos.jpg";
import applePayLogoLight from "../assets/images/apple_pay_light.png";
import applePayLogoDark from "../assets/images/apple_pay_dark.png";
import { apiClient } from "@/lib/api-client";
import { useServices } from "@/hooks/use-services";
import { useAddresses, useCreateAddress } from "@/hooks/use-addresses";
import {
  useGovernorates,
  useDistricts,
  useAvailableTimeSlots,
  useDeliveryFee,
} from "@/hooks/use-locations";
import { useWalletBalance } from "@/hooks/use-wallet";
import { useToast } from "@/hooks/use-toast";

const arabicDayNames: Record<string, string> = {
  'Sun': 'الأحد', 'Mon': 'الاثنين', 'Tue': 'الثلاثاء', 'Wed': 'الأربعاء',
  'Thu': 'الخميس', 'Fri': 'الجمعة', 'Sat': 'السبت',
};

// Manual address fields (mirrors Booking's schema). Only block/street/building
// are required; the rest are optional.
const addressSchema = z.object({
  governorateId: z.string().min(1),
  districtId: z.string().min(1),
  block: z.string().min(1),
  street: z.string().min(1),
  avenue: z.string().optional(),
  building: z.string().min(1),
  landmark: z.string().optional(),
  saveAddress: z.boolean().default(false),
  addressLabel: z.string().optional(),
});

type AddressFormValues = z.infer<typeof addressSchema>;

export default function GiftBooking() {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === "ar";
  const { toast } = useToast();

  // Read gift info from sessionStorage (set by RedeemGift page)
  const giftData = useMemo(() => {
    const raw = sessionStorage.getItem("giftBooking");
    return raw ? JSON.parse(raw) : {};
  }, []);
  const serviceId = giftData.serviceId || "";
  const vehicleType = giftData.vehicleType || "sedan";
  const giftCode = giftData.code || "";

  const { data: services } = useServices();
  const { data: addresses } = useAddresses();
  const { data: walletData } = useWalletBalance();
  const { mutate: createAddress } = useCreateAddress();

  // Address mode: saved vs manual.
  const [useManualAddress, setUseManualAddress] = useState(false);
  const [selectedAddressId, setSelectedAddressId] = useState<string>("");
  const [customerNote, setCustomerNote] = useState("");
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTimeSlotId, setSelectedTimeSlotId] = useState("");
  const [paymentMethod, setPaymentMethod] = useState<"wallet" | "knet" | "credit_card" | "apple_pay" | "online">("wallet");
  const [submitting, setSubmitting] = useState(false);
  const [bookingLat, setBookingLat] = useState<number | undefined>(undefined);
  const [bookingLng, setBookingLng] = useState<number | undefined>(undefined);

  const form = useForm<AddressFormValues>({
    resolver: zodResolver(addressSchema),
    defaultValues: {
      governorateId: "",
      districtId: "",
      block: "",
      street: "",
      avenue: "",
      building: "",
      landmark: "",
      saveAddress: false,
      addressLabel: "",
    },
  });

  const selectedGovernorateId = form.watch("governorateId");
  const selectedDistrictId = form.watch("districtId");

  const { data: governorates, isLoading: governoratesLoading } = useGovernorates();
  const { data: districts, isLoading: districtsLoading } = useDistricts(selectedGovernorateId);

  // With no saved addresses the manual form is the only address input, so it
  // must behave as manual mode even though the toggle (and its state) never
  // appears. Keying everything off `useManualAddress` alone left addressReady
  // false forever for first-time users — the submit button could never enable.
  const hasSavedAddresses = !!addresses && addresses.length > 0;
  const manualMode = useManualAddress || !hasSavedAddresses;

  // The active district for delivery-fee calc — either from a saved address or
  // the manual form's selection.
  const selectedAddress = addresses?.find((a) => a.id === selectedAddressId);
  const activeDistrictId = manualMode
    ? selectedDistrictId
    : selectedAddress?.districtId;
  const { data: deliveryFeeData } = useDeliveryFee(activeDistrictId);
  const deliveryFee = deliveryFeeData?.deliveryFee ?? 0;
  const walletBalance = walletData?.balance ?? 0;
  const insufficientForDelivery = deliveryFee > 0 && paymentMethod === "wallet" && deliveryFee > walletBalance;

  const service = services?.find((s) => s.id === serviceId);
  const vp = (service?.vehiclePricing || {}) as Record<string, number | undefined>;
  const price = vp[vehicleType] ?? service?.price ?? 0;

  // Next 14 days
  const dateOptions = useMemo(() => {
    const dates: Date[] = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    for (let i = 0; i < 14; i++) {
      const d = new Date(today);
      d.setDate(today.getDate() + i);
      dates.push(d);
    }
    return dates;
  }, []);

  const getDayName = (date: Date): string => {
    const en = format(date, "EEE", { locale: enUS });
    return isRtl ? (arabicDayNames[en] || en) : en;
  };

  const formattedDate = selectedDate ? format(selectedDate, "yyyy-MM-dd") : undefined;
  const { data: timeSlots, isLoading: slotsLoading } = useAvailableTimeSlots(formattedDate);

  // --- Address handlers (ported from Booking.tsx) ---
  const handleAddressSelect = (addressId: string) => {
    const addr = addresses?.find((a) => a.id === addressId);
    if (addr) {
      setSelectedAddressId(addressId);
      setUseManualAddress(false);
      form.setValue("governorateId", addr.governorateId || "");
      form.setValue("districtId", addr.districtId || "");
      form.setValue("saveAddress", false);
      form.setValue("addressLabel", "");
      setBookingLat(undefined);
      setBookingLng(undefined);
    }
  };

  const handleManualAddressToggle = () => {
    setUseManualAddress(true);
    setSelectedAddressId("");
    form.reset();
  };

  // Determine whether the address input is complete enough to submit.
  const addressReady = manualMode
    ? !!form.watch("governorateId") &&
      !!form.watch("districtId") &&
      !!form.watch("block") &&
      !!form.watch("street") &&
      !!form.watch("building")
    : !!selectedAddressId;

  const handleSubmit = async () => {
    if (!selectedDate || !selectedTimeSlotId || !addressReady) return;
    setSubmitting(true);

    try {
      let orderAddressId = selectedAddressId || undefined;
      let orderGovernorateId = selectedAddress?.governorateId;
      let orderDistrictId = selectedAddress?.districtId;

      // Manual address: optionally save it, then use it.
      if (manualMode) {
        const values = form.getValues();
        orderGovernorateId = values.governorateId;
        orderDistrictId = values.districtId;

        if (values.saveAddress) {
          // The backend requires a non-empty label; fall back to the district
          // name like Booking.tsx does when the optional label is left blank.
          const district = districts?.find((d) => d.id === values.districtId);
          const defaultLabel = district
            ? (isRtl ? district.nameAr : district.nameEn)
            : t("addresses.defaultLabel");

          const addrRes = await new Promise<any>((resolve, reject) => {
            createAddress(
              {
                governorateId: values.governorateId,
                districtId: values.districtId,
                block: values.block,
                street: values.street,
                avenue: values.avenue,
                building: values.building,
                landmark: values.landmark,
                label: values.addressLabel?.trim() || defaultLabel,
                latitude: bookingLat,
                longitude: bookingLng,
              },
              {
                onSuccess: resolve,
                onError: reject,
              },
            );
          });
          orderAddressId = addrRes?.id;
        }
      }

      // Create the order.
      // The gift covers the service price; the recipient only pays the delivery
      // fee from their wallet. The backend validates + consumes the gift
      // atomically as part of order creation (no separate redeem call).
      const orderRes = await apiClient.post("/orders", {
        items: [{ serviceId, vehicleType, quantity: 1 }],
        timeSlotId: selectedTimeSlotId,
        addressId: orderAddressId,
        governorateId: orderGovernorateId,
        districtId: orderDistrictId,
        // For manual (unsaved) addresses, include raw fields so the backend can
        // record them on the order.
        ...(manualMode && !orderAddressId
          ? {
              block: form.getValues("block"),
              street: form.getValues("street"),
              avenue: form.getValues("avenue") || undefined,
              building: form.getValues("building"),
              landmark: form.getValues("landmark") || undefined,
            }
          : {}),
        giftCode,
        paymentMethod: deliveryFee > 0 ? paymentMethod : "wallet",
        useWalletCredit: deliveryFee > 0 && paymentMethod === "wallet",
        walletCreditAmount: deliveryFee > 0 && paymentMethod === "wallet" ? deliveryFee : 0,
        notes: customerNote.trim()
          ? `${customerNote.trim()}\n[Gift: ${giftCode}]`
          : `Gift code: ${giftCode}`,
      });

      if (!orderRes.ok) {
        const err = await orderRes.json();
        throw new Error(err.message || "Failed to create order");
      }

      // Gateway payment: redirect to the payment link.
      const orderData = await orderRes.json();
      if (orderData.paymentLink && (orderData.paymentStatus === "pending" || !orderData.paymentStatus)) {
        window.location.href = orderData.paymentLink;
        return;
      }

      sessionStorage.removeItem("giftBooking");
      setSuccess(true);
    } catch (err: any) {
      toast({
        title: t("giftPage.purchaseFailed"),
        description: err.message,
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const [success, setSuccess] = useState(false);

  if (success) {
    return (
      <div className="min-h-screen pt-24 pb-24 bg-background flex items-center justify-center" dir={isRtl ? "rtl" : "ltr"}>
        <div className="text-center max-w-md px-4">
          <div className="w-20 h-20 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-10 h-10 text-green-500" />
          </div>
          <h1 className="text-2xl font-bold mb-2">{t("giftBooking.success")}</h1>
          <p className="text-muted-foreground mb-6">{t("giftBooking.successDesc")}</p>
          <Link href="/orders"><Button className="w-full">{t("giftBooking.viewOrders")}</Button></Link>
          <Link href="/"><Button variant="ghost" className="w-full mt-2">{t("giftPage.backHome")}</Button></Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-24 bg-background" dir={isRtl ? "rtl" : "ltr"}>
      <div className="container mx-auto px-4 max-w-2xl">
        <Link href="/redeem" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6">
          <ArrowLeft className="h-4 w-4" />
          {t("redeemPage.title")}
        </Link>

        <h1 className="text-2xl font-bold flex items-center gap-2 mb-2">
          <Gift className="w-6 h-6 text-primary" />
          {t("giftBooking.title")}
        </h1>
        <p className="text-sm text-muted-foreground mb-6">{t("giftBooking.subtitle")}</p>

        {/* Service summary (no vehicle selection, no payment) */}
        <div className="bg-card rounded-xl border border-primary/30 p-4 mb-6 flex items-center gap-3">
          {service?.imageUrl && (
            <img src={service.imageUrl} alt="" className="w-14 h-14 rounded-lg object-cover" />
          )}
          <div className="flex-1">
            <p className="font-medium">{isRtl ? service?.nameAr : service?.nameEn}</p>
            <p className="text-xs text-muted-foreground">{t(`booking.${vehicleType}`)} · {service?.duration} min</p>
          </div>
          <div className="text-end">
            <span className="inline-flex items-center gap-1 rounded-full bg-green-100 dark:bg-green-900/30 px-2.5 py-1 text-xs font-medium text-green-700 dark:text-green-400">
              <Gift className="h-3 w-3" />
              {t("giftBooking.gift")}
            </span>
          </div>
        </div>

        {/* Date */}
        <div className="mb-6">
          <Label className="mb-3 block">{t("booking.preferredDate")}</Label>
          <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide">
            {dateOptions.map((date, i) => {
              const isSelected = selectedDate && format(date, "yyyy-MM-dd") === format(selectedDate, "yyyy-MM-dd");
              const isToday = format(date, "yyyy-MM-dd") === format(new Date(), "yyyy-MM-dd");
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => { setSelectedDate(date); setSelectedTimeSlotId(""); }}
                  className={cn(
                    "flex-shrink-0 flex flex-col items-center justify-center p-3 rounded-xl border-2 min-w-[70px] transition-all",
                    isSelected ? "border-primary bg-primary/10 text-white" : "border-border hover:border-primary/50 hover:bg-muted/50",
                  )}
                >
                  <span className="text-xs font-medium">{isToday ? t("booking.today") : getDayName(date)}</span>
                  <span className="text-lg font-semibold">{format(date, "d")}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Time slots (Preferred Arrival Time) */}
        {selectedDate && (
          <div className="mb-6">
            <Label className="mb-3 block">{t("booking.preferredTime")}</Label>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
              {slotsLoading ? (
                <div className="col-span-full text-center text-sm text-muted-foreground py-4"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div>
              ) : (timeSlots || []).length === 0 ? (
                <div className="col-span-full text-center text-sm text-muted-foreground py-4">{t("booking.noTimeSlots")}</div>
              ) : (
                (timeSlots || []).map((slot: any) => (
                  <button
                    key={slot.id}
                    type="button"
                    onClick={() => setSelectedTimeSlotId(slot.id)}
                    className={cn(
                      "flex flex-col items-center justify-center p-3 rounded-xl border-2 transition-all",
                      selectedTimeSlotId === slot.id ? "border-primary bg-primary/10 text-white" : "border-border hover:border-primary/50 hover:bg-muted/50",
                    )}
                  >
                    <span className="text-sm font-medium">{slot.time}</span>
                    {slot.isLateNight && (
                      <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-indigo-500/15 text-indigo-600 dark:text-indigo-400">
                        {t("booking.lateNight")}
                      </span>
                    )}
                  </button>
                ))
              )}
            </div>
          </div>
        )}

        {/* Address */}
        <Form {...form}>
          <div className="mb-6">
            <Label className="mb-3 block flex items-center gap-1"><MapPin className="h-4 w-4" /> {t("booking.address")}</Label>

            {/* Use Saved / Enter Manually toggle — only if the user has saved addresses */}
            {addresses && addresses.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <Button
                    type="button"
                    variant={!useManualAddress ? "default" : "outline"}
                    size="sm"
                    onClick={() => {
                      setUseManualAddress(false);
                      if (selectedAddressId && addresses?.find((a) => a.id === selectedAddressId)) {
                        handleAddressSelect(selectedAddressId);
                      } else if (addresses && addresses.length > 0) {
                        const defaultAddr = addresses.find((a) => a.isDefault) || addresses[0];
                        if (defaultAddr) handleAddressSelect(defaultAddr.id);
                      }
                    }}
                    className="rounded-full"
                  >
                    {t("addresses.useSaved")}
                  </Button>
                  <Button
                    type="button"
                    variant={useManualAddress ? "default" : "outline"}
                    size="sm"
                    onClick={handleManualAddressToggle}
                    className="rounded-full"
                  >
                    {t("addresses.enterManually")}
                  </Button>
                </div>

                {/* Saved-address dropdown + summary */}
                {!useManualAddress && (
                  <>
                    <MobileSelect
                      value={selectedAddressId || undefined}
                      onValueChange={(value) => { if (value) handleAddressSelect(value); }}
                      placeholder={t("addresses.selectAddress")}
                      className="rounded-xl h-12"
                    >
                      {addresses?.map((addr: any) => (
                        <MobileSelectItem key={addr.id} value={addr.id}>
                          <span className="flex items-center gap-2">
                            {addr.label}
                            {addr.isDefault && (
                              <span className="text-xs text-primary">({t("addresses.default")})</span>
                            )}
                          </span>
                        </MobileSelectItem>
                      ))}
                    </MobileSelect>

                    {selectedAddressId && (() => {
                      const addr = addresses?.find((a) => a.id === selectedAddressId);
                      if (!addr) return null;
                      const gov = addr.governorate || governorates?.find((g) => g.id === addr.governorateId);
                      const dist = districts?.find((d) => d.id === addr.districtId) || addr.district;
                      const govName = isRtl ? gov?.nameAr : gov?.nameEn;
                      const distName = isRtl ? dist?.nameAr : dist?.nameEn;
                      return (
                        <div className="bg-muted/50 rounded-lg p-3 text-sm">
                          <p className="font-medium">{addr.label}</p>
                          {(govName || distName) && (
                            <p className="text-muted-foreground">{[govName, distName].filter(Boolean).join(", ")}</p>
                          )}
                          <p className="text-muted-foreground">
                            {[
                              addr.block && `${t("addresses.block")} ${addr.block}`,
                              addr.street && `${t("addresses.street")} ${addr.street}`,
                              addr.avenue && `${t("addresses.avenue")} ${addr.avenue}`,
                              addr.building && `${t("addresses.building")} ${addr.building}`,
                            ].filter(Boolean).join(", ")}
                          </p>
                          {addr.landmark && <p className="text-muted-foreground text-xs">{t("addresses.landmark")}: {addr.landmark}</p>}
                        </div>
                      );
                    })()}
                  </>
                )}
              </div>
            )}

            {/* Manual form (when toggled, or when there are no saved addresses) */}
            {manualMode && (
              <div className={cn("space-y-4", addresses && addresses.length > 0 && "border-t border-border/50 pt-4")}>
                <div className={cn("grid md:grid-cols-2 gap-4", isRtl && "md:grid-flow-dense")}>
                  <FormField
                    control={form.control}
                    name="governorateId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm">{t("booking.governorate")}</FormLabel>
                        <FormControl>
                          <MobileSelect
                            onValueChange={field.onChange}
                            value={field.value}
                            placeholder={t("booking.selectGovernorate")}
                            className={cn("rounded-xl h-12", isRtl && "text-right")}
                          >
                            {governoratesLoading ? (
                              <MobileSelectItem value="loading" disabled>{t("booking.loading")}</MobileSelectItem>
                            ) : !governorates || governorates.length === 0 ? (
                              <MobileSelectItem value="no-governorates" disabled>{t("booking.noGovernorates")}</MobileSelectItem>
                            ) : (
                              governorates.map((gov) => (
                                <MobileSelectItem key={gov.id} value={gov.id}>
                                  {isRtl ? gov.nameAr : gov.nameEn}
                                </MobileSelectItem>
                              ))
                            )}
                          </MobileSelect>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="districtId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm">{t("booking.district")}</FormLabel>
                        <FormControl>
                          <MobileSelect
                            onValueChange={field.onChange}
                            value={field.value}
                            disabled={!selectedGovernorateId}
                            placeholder={selectedGovernorateId ? t("booking.selectDistrict") : t("booking.selectGovernorateFirst")}
                            className={cn("rounded-xl h-12", isRtl && "text-right")}
                          >
                            {districtsLoading ? (
                              <MobileSelectItem value="loading" disabled>{t("booking.loading")}</MobileSelectItem>
                            ) : !districts || districts.length === 0 ? (
                              <MobileSelectItem value="no-districts" disabled>{t("booking.noDistricts")}</MobileSelectItem>
                            ) : (
                              districts.map((district) => (
                                <MobileSelectItem key={district.id} value={district.id}>
                                  {isRtl ? district.nameAr : district.nameEn}
                                </MobileSelectItem>
                              ))
                            )}
                          </MobileSelect>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className={cn("grid md:grid-cols-4 gap-4", isRtl && "md:grid-flow-dense")}>
                  <FormField
                    control={form.control}
                    name="block"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm">{t("addresses.block")} *</FormLabel>
                        <FormControl>
                          <Input placeholder={t("addresses.blockPlaceholder")} {...field} className={cn("rounded-xl h-12", isRtl && "text-right")} dir={isRtl ? "rtl" : "ltr"} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="street"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm">{t("addresses.street")} *</FormLabel>
                        <FormControl>
                          <Input placeholder={t("addresses.streetPlaceholder")} {...field} className={cn("rounded-xl h-12", isRtl && "text-right")} dir={isRtl ? "rtl" : "ltr"} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="avenue"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm">{t("addresses.avenue")}</FormLabel>
                        <FormControl>
                          <Input placeholder={t("addresses.avenuePlaceholder")} {...field} className={cn("rounded-xl h-12", isRtl && "text-right")} dir={isRtl ? "rtl" : "ltr"} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="building"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm">{t("addresses.building")} *</FormLabel>
                        <FormControl>
                          <Input placeholder={t("addresses.buildingPlaceholder")} {...field} className={cn("rounded-xl h-12", isRtl && "text-right")} dir={isRtl ? "rtl" : "ltr"} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="landmark"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm">{t("addresses.landmark")}</FormLabel>
                      <FormControl>
                        <Input placeholder={t("addresses.landmarkPlaceholder")} {...field} className={cn("rounded-xl h-12", isRtl && "text-right")} dir={isRtl ? "rtl" : "ltr"} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="gift-saveAddress"
                    checked={form.watch("saveAddress")}
                    onCheckedChange={(checked) => form.setValue("saveAddress", !!checked)}
                  />
                  <label htmlFor="gift-saveAddress" className="text-sm cursor-pointer">
                    {t("addresses.saveForFuture")}
                  </label>
                </div>

                {form.watch("saveAddress") && (
                  <>
                    <FormField
                      control={form.control}
                      name="addressLabel"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm">{t("addresses.labelOptional")}</FormLabel>
                          <FormControl>
                            <Input placeholder={t("addresses.labelPlaceholder")} {...field} className={cn("rounded-xl h-12", isRtl && "text-right")} dir={isRtl ? "rtl" : "ltr"} />
                          </FormControl>
                          <FormDescription className="text-xs">
                            {t("addresses.labelDescription")}
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {/* Map location — only when saving address */}
                    <MapPicker
                      latitude={bookingLat}
                      longitude={bookingLng}
                      onChange={(lat, lng) => { setBookingLat(lat); setBookingLng(lng); }}
                    />
                  </>
                )}
              </div>
            )}
          </div>
        </Form>

        {/* Notes */}
        <div className="mb-6">
          <Label className="mb-2 block">{t("booking.notes")}</Label>
          <textarea
            value={customerNote}
            onChange={(e) => setCustomerNote(e.target.value)}
            placeholder={t("booking.notesPlaceholder")}
            rows={2}
            className="w-full rounded-xl border border-border/60 bg-background p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        {/* Delivery fee info */}
        {addressReady && deliveryFee > 0 && (
          <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4 mb-6">
            <p className="text-sm font-medium text-amber-700 dark:text-amber-400">
              {t("giftBooking.deliveryFee")}: {deliveryFee.toFixed(3)} {t("services.currency")}
            </p>
            <p className="text-xs text-amber-600/80 dark:text-amber-400/70 mt-1">
              {t("giftBooking.deliveryFeeNote")}
            </p>
          </div>
        )}

        {/* Payment method (only when there's a delivery fee to pay) */}
        {addressReady && deliveryFee > 0 && (
          <div className="mb-6">
            <Label className="mb-3 block">{t("booking.selectPaymentMethod")}</Label>
            <div className="grid grid-cols-2 gap-3">
              {/* Wallet */}
              <button
                type="button"
                onClick={() => setPaymentMethod("wallet")}
                className={cn(
                  "flex flex-col items-center justify-center p-4 rounded-xl border-2 transition-all gap-2",
                  paymentMethod === "wallet"
                    ? "border-primary bg-primary/5"
                    : "border-border hover:border-primary/50",
                )}
              >
                <WalletIcon className="w-7 h-7 text-primary" />
                <span className="text-xs font-medium">{t("giftPage.payWithWallet")}</span>
                <span className="text-[10px] text-muted-foreground">{walletBalance.toFixed(3)} {t("services.currency")}</span>
              </button>

              {/* Online (UPayments hosted page: KNET / card / Apple Pay) */}
              <button
                type="button"
                onClick={() => setPaymentMethod("online")}
                className={cn(
                  "flex flex-col items-center justify-center p-4 rounded-xl border-2 transition-all gap-2",
                  paymentMethod === "online"
                    ? "border-primary bg-primary/5"
                    : "border-border hover:border-primary/50",
                )}
              >
                <div className="flex items-center justify-center gap-2">
                  <img src={knetLogo} alt="KNET" className="h-6 object-contain rounded" />
                  <img src={creditCardLogos} alt="Visa / Mastercard" className="h-6 object-contain rounded" />
                  <img
                    src={document.documentElement.classList.contains("dark") ? applePayLogoDark : applePayLogoLight}
                    alt="Apple Pay"
                    className="h-6 object-contain"
                  />
                </div>
                <span className="text-xs font-medium">{t("booking.payment.online")}</span>
                <span className="text-[10px] text-muted-foreground text-center">{t("booking.payment.onlineHint")}</span>
              </button>
            </div>

            {paymentMethod === "wallet" && insufficientForDelivery && (
              <p className="text-sm text-destructive mt-2">
                {t("giftBooking.insufficientDelivery")} ({walletBalance.toFixed(3)} {t("services.currency")})
              </p>
            )}
            <p className="text-xs text-muted-foreground mt-2">{t("booking.payment.secureNote")}</p>
          </div>
        )}

        {/* Submit */}
        <Button
          size="lg"
          className="w-full"
          onClick={handleSubmit}
          disabled={submitting || !selectedDate || !selectedTimeSlotId || !addressReady || insufficientForDelivery}
        >
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Gift className="h-4 w-4 me-2" />}
          {insufficientForDelivery ? t("giftBooking.insufficientDelivery") : t("giftBooking.bookNow")}
        </Button>

      </div>
    </div>
  );
}
